import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { authenticate } from './api/authedFetch';
// Landing page stays eager — it's what students see first.
import TestSelection from './pages/TestSelection';
import ErrorBoundary from './components/ErrorBoundary';

// Everything else is route-split so a student opening the mini app doesn't
// download the admin dashboards. Each page becomes its own chunk.
const TestRunner = lazy(() => import('./pages/TestRunner'));
const TestResults = lazy(() => import('./pages/TestResults'));
const Progress = lazy(() => import('./pages/Progress'));
const ReportCard = lazy(() => import('./pages/ReportCard'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminContent = lazy(() => import('./pages/AdminContent'));
const AdminStudents = lazy(() => import('./pages/AdminStudents'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const SkillPractice = lazy(() => import('./pages/SkillPractice'));
const WeaknessDashboard = lazy(() => import('./pages/WeaknessDashboard'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const TeacherDashboard = lazy(() => import('./pages/TeacherDashboard'));
const ResellerDashboard = lazy(() => import('./pages/ResellerDashboard'));

function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-pulse text-tg-hint">Memuat…</div>
    </div>
  );
}

function RequireRole({ roles, children }: { roles: string[]; children: ReactNode }) {
  const role = useAuthStore(state => state.user?.role);
  if (!role || !roles.includes(role)) {
    return <div className="p-6 text-center"><h1 className="text-xl font-semibold">Akses terbatas</h1>
      <p className="mt-2">Akun ini tidak memiliki izin untuk membuka halaman ini.</p>
      <a className="inline-block mt-4 underline" href="/">Kembali ke latihan</a></div>;
  }
  return <>{children}</>;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp: {
        initData: string;
        initDataUnsafe: Record<string, unknown>;
        ready: () => void;
        expand: () => void;
        close: () => void;
        MainButton: any;
        BackButton: any;
        HapticFeedback: any;
        themeParams: Record<string, string>;
        colorScheme: string;
      };
    };
  }
}

function App() {
  const [loading, setLoading] = useState(true);
  const [launchDataMissing, setLaunchDataMissing] = useState(false);
  const [authFailed, setAuthFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function initialize() {
      try {
        window.Telegram?.WebApp?.ready();
        window.Telegram?.WebApp?.expand();
      } catch { /* Authentication still verifies the signed launch data. */ }
      for (let attempt = 0; attempt < 4 && !cancelled; attempt++) {
        if (window.Telegram?.WebApp?.initData && await authenticate()) {
          if (!cancelled) setLoading(false);
          return;
        }
        if (attempt < 3) await new Promise(resolve => setTimeout(resolve, 500));
      }
      if (!cancelled) {
        setLaunchDataMissing(!window.Telegram?.WebApp?.initData);
        setAuthFailed(!!window.Telegram?.WebApp?.initData);
        setLoading(false);
      }
    }
    void initialize();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tg-button mx-auto mb-4"></div>
          <p className="text-tg-hint">Loading...</p>
        </div>
      </div>
    );
  }

  if (launchDataMissing) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg p-6">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">📱</div>
          <h1 className="text-xl font-semibold mb-2 text-tg-text">Buka EduBot dari tombol bot</h1>
          <p className="text-tg-hint mb-4">
            Ketuk Buka Bot, lalu ketuk Buka EduBot pada pesan bot untuk masuk. Jika diminta, ketuk Start terlebih dahulu.
          </p>
          <a
            href="https://t.me/OSEE_TOEFL_IELTS_TOEIC_study_bot?start=launch"
            className="inline-block px-6 py-3 rounded-lg bg-tg-button text-tg-button-text font-semibold"
          >
            Buka Bot
          </a>
        </div>
      </div>
    );
  }

  if (authFailed) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg p-6">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-semibold mb-2 text-tg-text">Gagal masuk</h1>
          <p className="text-tg-hint mb-4">
            Ada masalah saat otentikasi. Coba tutup mini app dan buka lagi dari menu bot.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-block px-6 py-3 rounded-lg bg-tg-button text-tg-button-text font-semibold"
          >
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-tg-bg text-tg-text">
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<TestSelection />} />
        <Route path="/test" element={<TestSelection />} />
        <Route path="/skill-practice" element={<ErrorBoundary fallbackMessage="Skill Practice gagal dimuat."><SkillPractice /></ErrorBoundary>} />
        <Route path="/test/:attemptId" element={<ErrorBoundary fallbackMessage="Soal gagal dimuat. Coba muat ulang halaman."><TestRunner /></ErrorBoundary>} />
        <Route path="/test/:attemptId/results" element={<ErrorBoundary fallbackMessage="Hasil tes gagal dimuat."><TestResults /></ErrorBoundary>} />
        <Route path="/progress" element={<ErrorBoundary fallbackMessage="Progress gagal dimuat."><Progress /></ErrorBoundary>} />
        <Route path="/report-card" element={<ErrorBoundary fallbackMessage="Laporan gagal dimuat."><ReportCard /></ErrorBoundary>} />
        <Route path="/referral" element={
          <div className="flex items-center justify-center min-h-screen bg-tg-bg p-6">
            <div className="text-center max-w-md">
              <div className="text-5xl mb-4">🎁</div>
              <h1 className="text-xl font-semibold mb-2 text-tg-text">Referral Program</h1>
              <p className="text-tg-hint mb-4">Undang teman kamu dan dapatkan bonus soal gratis! Ketik /referral di bot untuk kode referral.</p>
              <a href="https://t.me/OSEE_TOEFL_IELTS_TOEIC_study_bot?start=referral" className="inline-block px-6 py-3 rounded-lg bg-tg-button text-tg-button-text font-semibold">Buka Bot</a>
            </div>
          </div>
        } />
        <Route path="/admin/content" element={<RequireRole roles={["teacher","admin"]}><AdminContent /></RequireRole>} />
        <Route path="/admin/students" element={<RequireRole roles={["teacher","admin"]}><AdminStudents /></RequireRole>} />
        <Route path="/admin/analytics" element={<RequireRole roles={["admin"]}><AdminAnalytics /></RequireRole>} />
        <Route path="/admin" element={<RequireRole roles={["admin"]}><AdminPanel /></RequireRole>} />
        <Route path="/admin/weaknesses" element={<RequireRole roles={["teacher","admin"]}><WeaknessDashboard /></RequireRole>} />
        <Route path="/admin/teacher" element={<RequireRole roles={["teacher","admin"]}><TeacherDashboard /></RequireRole>} />
        <Route path="/admin/reseller" element={<RequireRole roles={["reseller","admin"]}><ResellerDashboard /></RequireRole>} />
        <Route path="/dashboard" element={<RequireRole roles={["admin"]}><Dashboard /></RequireRole>} />
        <Route path="/admin/panel" element={<RequireRole roles={["admin"]}><AdminPanel /></RequireRole>} />
      </Routes>
      </Suspense>
    </div>
  );
}

export default App;
